import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Reply, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Comment, VoteType } from '../../types/discussion';
import { CommentForm } from './CommentForm';
import { Button } from '../ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../utils/cn';

interface CommentItemProps {
  comment: Comment;
  onVote: (commentId: string, voteType: VoteType) => Promise<boolean>;
  onReply: (text: string, parentId: string) => Promise<boolean>;
  depth?: number;
}

/**
 * CommentItem - Recursive comment component with voting, replies, and badges
 * Features:
 * - User avatar and username
 * - Win/loss badge (attempts count)
 * - Sentiment vote badge (buy/hold/sell)
 * - Upvote/downvote buttons with score
 * - Reply button (disabled at max depth)
 * - Nested replies (recursive rendering)
 * - Edit indicator
 */
export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  onVote,
  onReply,
  depth = 0
}) => {
  const { user } = useAuth();
  const [showReplyForm, setShowReplyForm] = useState(false);
  const canReply = depth < 3 && user; // Max depth is 3

  const handleReplySubmit = async (text: string) => {
    const success = await onReply(text, comment.id);
    if (success) {
      setShowReplyForm(false);
    }
    return success;
  };

  const handleVote = async (voteType: VoteType) => {
    await onVote(comment.id, voteType);
  };

  return (
    <div className={cn('space-y-3', depth > 0 && 'ml-6 pl-4 border-l-2 border-border')}>
      <div className="rounded-lg border bg-card p-4 space-y-3">
        {/* Header: Avatar, Username, Badges */}
        <div className="flex items-start gap-3">
          {/* Avatar */}
          {comment.avatar ? (
            <img
              src={comment.avatar}
              alt={comment.username}
              className="h-8 w-8 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
              <User className="h-4 w-4 text-muted-foreground" />
            </div>
          )}

          {/* Username and Badges */}
          <div className="flex-1 space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-foreground truncate">
                {comment.username}
              </span>

              {/* Win/Loss Badge */}
              {comment.won ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 whitespace-nowrap">
                  ✅ {comment.guesses} {comment.guesses === 1 ? 'guess' : 'guesses'}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 whitespace-nowrap">
                  ❌ DNF
                </span>
              )}

              {/* Sentiment Badge */}
              {comment.sentimentVote && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs whitespace-nowrap',
                    comment.sentimentVote === 'buy' &&
                      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
                    comment.sentimentVote === 'hold' &&
                      'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                    comment.sentimentVote === 'sell' &&
                      'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                  )}
                >
                  {comment.sentimentVote === 'buy' && '📈 Buy'}
                  {comment.sentimentVote === 'hold' && '📊 Hold'}
                  {comment.sentimentVote === 'sell' && '📉 Sell'}
                </span>
              )}

              {/* Timestamp */}
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
              </span>

              {/* Edited Indicator */}
              {comment.isEdited && (
                <span className="text-xs text-muted-foreground italic whitespace-nowrap">
                  (edited)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Comment Text */}
        <p className="text-sm text-foreground whitespace-pre-wrap break-words">
          {comment.commentText}
        </p>

        {/* Actions: Vote, Reply */}
        <div className="flex items-center gap-4">
          {/* Vote Buttons */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleVote('up')}
              className={cn(
                'h-8 w-8 p-0',
                comment.currentUserVote === 'up' && 'text-blue-600 dark:text-blue-400'
              )}
              disabled={!user}
              aria-label="Upvote"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>

            <span
              className={cn(
                'text-sm font-medium min-w-[30px] text-center',
                comment.voteScore > 0 && 'text-green-600 dark:text-green-400',
                comment.voteScore < 0 && 'text-red-600 dark:text-red-400'
              )}
            >
              {comment.voteScore}
            </span>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleVote('down')}
              className={cn(
                'h-8 w-8 p-0',
                comment.currentUserVote === 'down' && 'text-red-600 dark:text-red-400'
              )}
              disabled={!user}
              aria-label="Downvote"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>

          {/* Reply Button */}
          {canReply && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="h-8 text-xs"
            >
              <Reply className="h-3 w-3 mr-1" />
              Reply
            </Button>
          )}

          {/* Max Depth Message */}
          {depth >= 3 && (
            <span className="text-xs text-muted-foreground">
              Max reply depth reached
            </span>
          )}
        </div>

        {/* Reply Form */}
        {showReplyForm && (
          <div className="pt-2">
            <CommentForm
              onSubmit={handleReplySubmit}
              onCancel={() => setShowReplyForm(false)}
              placeholder="Write a reply..."
              submitLabel="Reply"
            />
          </div>
        )}
      </div>

      {/* Nested Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-3">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onVote={onVote}
              onReply={onReply}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};
